import {
	Asset,
	CrossIcon,
	PaddedContainer,
	Text,
	genClassName,
	SPACING,
} from "@disco/disco_core";
import React, { useCallback, useEffect, useState } from "react";

import { HorizontalSeparator } from "../CoreUI";
import "./essential-widget-card.css";
import useDimensions from "../../hooks/useDimensions";
import { PRIVACY_POLICY_URL, WIDGET_VERSION, WIDGET_TYPE } from "../../conf";
import EssentialWidgetOffers from "./EssentialWidgetOffers";
import message from "../../utils/message";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import useNurtureOffers from "../../hooks/useNurtureOffers";
import useData from "../../hooks/useData";
import useMode from "../../hooks/useMode";
import useWidget from "../../hooks/useWidget";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import { WIDGET_ELEMENT_CLICK_TARGET } from "../../event_conf";
import accessibilityProvider from "../../utils/accessibilityProvider";
import useEssentialWidgetOffer from "../../hooks/useEssentialWidgetOffer";
import useExperience from "../../hooks/useExperience";
import { EssentialWidgetOfferContext } from ".";
import OfferRow from "./OfferRow";
export const ESSENTIAL_WIDGET_BASELINE_H_PADDING = 20;

const EssentialWidgetCardHeader = ({ onClose }) => {
	const track = useTrack();

	const { defaultOfferEventProps } = useEssentialWidgetOffer();

	const handleClose = () => {
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultOfferEventProps,
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.CLOSE_BUTTON,
		});

		if (typeof onClose !== "function") return;
		onClose();
	};
	return (
		<PaddedContainer
			vPadding={14}
			hPadding={ESSENTIAL_WIDGET_BASELINE_H_PADDING}
			flexContent
		>
			<Text
				style={{
					fontWeight: 700,
					fontSize: "18px",
					lineHeight: "20px",
				}}
			>
				Your order is confirmed
			</Text>
			<Asset marginLeft={"auto"} clickable onClick={handleClose}>
				<CrossIcon />
			</Asset>
		</PaddedContainer>
	);
};

const EssentialWidgetCardFooter = () => {
	const track = useTrack();
	const { isInline } = useMode();

	const handlePrivacyClick = () => {
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_FOOTER_PRIVACY,
		});
	};

	return (
		<PaddedContainer
			vPadding={12}
			hPadding={isInline ? 16 : ESSENTIAL_WIDGET_BASELINE_H_PADDING}
			flexContent
			style={{ justifyContent: "flex-end" }}
		>
			<Text
				Element={"a"}
				href={PRIVACY_POLICY_URL}
				color="mid"
				style={{ textDecoration: "underline" }}
				size="label"
				target="_blank"
				rel="noopener noreferrer"
				onClick={handlePrivacyClick}
				{...accessibilityProvider({
					role: "link",
					name: "Privacy Policy",
				})}
			>
				Privacy Policy
			</Text>
		</PaddedContainer>
	);
};

const EssentialWidgetCard = ({ triggerRemount }) => {
	const { inDesktopOnly, isLargeWidget } = useDimensions();
	const { containerRef } = useData();
	const { isInline } = useMode();
	const { activeWidgetVersion } = useExperience();
	const [shouldRenderOfferSlot, setShouldRenderOfferSlot] = useState(true);

	const {
		NUM_OFFERS_IDX,
		TOTAL_OFFERS,
		areOffersEmpty,

		activeOffer,
		activeOfferIdx,

		claimOffer,
		claimedOffers,
		isOfferClaimed,

		isFirstOffer,
		showNextOffer,
		showPrevOffer,
		isLastOffer,
	} = useNurtureOffers();

	const getIntersectionId = () => {
		const defaultIntersectionId = "essential-widget-card";

		if (
			isInline &&
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.3]
		) {
			return `${defaultIntersectionId}-inline`;
		}

		return defaultIntersectionId;
	};

	const intersectionId = getIntersectionId();

	const { delayedTriggerResize, triggerResize } = useWidget({
		isEmpty: areOffersEmpty,
		containerRef,
		canTrackLoad: true,
		brandsRemoteKey: "brands",
		intersectionId,
		resizeOnMount: isInline,
	});

	useEffect(() => {
		if (
			isInline &&
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.3]
		) {
			setShouldRenderOfferSlot(false);
		}
	}, [isInline, activeWidgetVersion]);

	const closeWidgetCard = useCallback(() => {
		if (
			isInline &&
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.2]
		) {
			setShouldRenderOfferSlot(false);
			return;
		}

		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.3]
		) {
			triggerRemount();
			message(MESSAGE_TYPES.MODE_INLINE);
			return;
		}

		message(MESSAGE_TYPES.CLOSE_WIDGET_FULLSCREEN);
	}, [isInline, activeWidgetVersion, triggerRemount]);

	useEffect(() => {
		if (!isInline) return;
		delayedTriggerResize(200)();
	}, [activeOfferIdx, delayedTriggerResize, isInline]);

	const COMPUTED_PADDING = isLargeWidget ? 20 : 16;

	const getRenderOfferRow = () => {
		if (
			isInline &&
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.2]
		) {
			return true;
		}

		if (
			isInline &&
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL][0.3]
		) {
			return true;
		}

		return false;
	};

	const renderOfferRow = getRenderOfferRow();

	return (
		<EssentialWidgetOfferContext.Provider
			value={{
				// Ensure you update hook when adding new value here.
				NUM_OFFERS_IDX,
				TOTAL_OFFERS,

				activeOffer,
				activeOfferIdx,

				claimOffer,
				claimedOffers,
				isOfferClaimed,

				isFirstOffer,
				showNextOffer,
				showPrevOffer,
				isLastOffer,

				handleWidgetCardClose: closeWidgetCard,
			}}
		>
			<PaddedContainer
				className={genClassName({
					base: "essential-widget-card",
					conditionals: {
						"essential-widget-card-inline": isInline,
					},
				})}
				style={{
					maxWidth: isInline
						? "100%"
						: `min(${inDesktopOnly ? 700 : 355}px, 95%)`,
				}}
				hMarginAuto
				data-intersectionuid={intersectionId}
				ref={containerRef}
			>
				{!isInline && (
					<EssentialWidgetCardHeader onClose={closeWidgetCard} />
				)}

				<EssentialWidgetOffers
					shouldRenderOfferSlot={shouldRenderOfferSlot}
					triggerResize={triggerResize}
				/>

				{renderOfferRow && (
					<>
						<OfferRow
							vPadding={SPACING.REGULAR}
							parentHPadding={COMPUTED_PADDING}
						/>
						<HorizontalSeparator />
					</>
				)}

				<EssentialWidgetCardFooter />
			</PaddedContainer>
		</EssentialWidgetOfferContext.Provider>
	);
};
export default EssentialWidgetCard;
